import {
  type ApiResponsePromise,
  type ApiProgressEvent,
  makeRequest,
  type ApiResponseArgs,
  type PinType,
  toCorrectFormDataValue,
} from '@/js/libs/api';
import * as Types from './types';
import * as Permissions from '@/js/libs/generated/permissions/types';
import {
  type DateTimeType,
  type DateType,
  type DateTimeUnixType,
  encodeBody,
  fromDateType as fromDate,
  toDateType as toDate,
  fromDateTimeType as fromDateTime,
  toDateTimeUnixType as toDateTime,
} from '@/js/libs/api-helpers';

export { type DateTimeType, type DateTimeUnixType, fromDate, toDate, fromDateTime, toDateTime };

export type IdCompanyDto = { _id: 'ws-companies:CompanyDto'; toString: () => string };
export type IdUserDto = { _id: 'ws-companies:UserDto'; toString: () => string };
export type IdAccountDto = { _id: 'ws-crm:AccountDto'; toString: () => string };
export type IdWebsiteDto = { _id: 'ws-digital:WebsiteDto'; toString: () => string };
export type IdUserGroupDto = { _id: 'ws-permissions:UserGroupDto'; toString: () => string };
export type getCurrentUserParameters = { selectedCompanyId?: IdCompanyDto };
export type getCurrentUserResponse = PinType<
  ApiResponsePromise<{
    responseCode: 202;
    data: Types.UserDto;
    headers: null;
  }>,
  Types.UserDto
>;

export type getCurrentUserSeparateParams = {
  parameters: getCurrentUserParameters;
};
export type getCurrentUserParamsUnion = getCurrentUserParameters;
export type getCurrentUser = {
  (arg: {
    params: PinType<getCurrentUserParamsUnion, getCurrentUserSeparateParams>;
    onUploadProgress?: (event: ApiProgressEvent) => void;
    signal?: AbortSignal;
    baseURL?: string;
    isInternal?: boolean;
    headers?: Record<string, string>;
    queryDefaults?: Partial<getCurrentUserParamsUnion> | false;
  }): getCurrentUserResponse;
};

export const getCurrentUser: getCurrentUser = async ({
  params,
  onUploadProgress,
  signal,
  baseURL,
  isInternal,
  headers: headersParam = {},
  queryDefaults,
  ...otherParams
}) => {
  const path = '/api/ws-permissions/users/current-user';
  const pathInternal = '/api/users/current-user';
  let body: any = {};
  const formData = new FormData();
  const urlParams = new URLSearchParams();
  {
    const value =
      queryDefaults === false
        ? params.selectedCompanyId
        : (params.selectedCompanyId ?? queryDefaults?.['selectedCompanyId']);
    if (value !== undefined) {
      urlParams.append('selectedCompanyId', value.toString());
    }
  }

  const headers: Record<string, string> = {};
  headers['Accept'] = 'application/json';

  return makeRequest<ApiResponseArgs<getCurrentUserResponse>>({
    ...otherParams,
    method: 'GET',
    url: isInternal ? pathInternal : path,
    params: urlParams,
    data: {},
    onUploadProgress,
    signal,
    baseURL: baseURL || undefined,
    headers: { ...headers, ...headersParam },
    responseTypes: 'json',
    requestType: 'json',
  });
};
(getCurrentUser as any).paramsOrder = [{ type: 'parameters' }];

export type getUserByEmailWithPermissionsParameters = { email: string; selectedCompanyId?: string };
export type getUserByEmailWithPermissionsResponse = PinType<
  ApiResponsePromise<{
    responseCode: 202;
    data: Types.CompactUserDto;
    headers: null;
  }>,
  Types.CompactUserDto
>;

export type getUserByEmailWithPermissionsSeparateParams = {
  parameters: getUserByEmailWithPermissionsParameters;
};
export type getUserByEmailWithPermissionsParamsUnion = getUserByEmailWithPermissionsParameters;
export type getUserByEmailWithPermissions = {
  (arg: {
    params: PinType<getUserByEmailWithPermissionsParamsUnion, getUserByEmailWithPermissionsSeparateParams>;
    onUploadProgress?: (event: ApiProgressEvent) => void;
    signal?: AbortSignal;
    baseURL?: string;
    isInternal?: boolean;
    headers?: Record<string, string>;
    queryDefaults?: Partial<getUserByEmailWithPermissionsParamsUnion> | false;
  }): getUserByEmailWithPermissionsResponse;
};

export const getUserByEmailWithPermissions: getUserByEmailWithPermissions = async ({
  params,
  onUploadProgress,
  signal,
  baseURL,
  isInternal,
  headers: headersParam = {},
  queryDefaults,
  ...otherParams
}) => {
  if (params.email === undefined) {
    throw new Error('Missing required parameter: email');
  }

  const path = '/api/ws-permissions/user-with-permissions/by-email';
  const pathInternal = '/api/user-with-permissions/by-email';
  let body: any = {};
  const formData = new FormData();
  const urlParams = new URLSearchParams();
  if (params.email !== undefined) {
    urlParams.append('email', params.email.toString());
  }
  {
    const value =
      queryDefaults === false
        ? params.selectedCompanyId
        : (params.selectedCompanyId ?? queryDefaults?.['selectedCompanyId']);
    if (value !== undefined) {
      urlParams.append('selectedCompanyId', value.toString());
    }
  }

  const headers: Record<string, string> = {};
  headers['Accept'] = 'application/json';

  return makeRequest<ApiResponseArgs<getUserByEmailWithPermissionsResponse>>({
    ...otherParams,
    method: 'GET',
    url: isInternal ? pathInternal : path,
    params: urlParams,
    data: {},
    onUploadProgress,
    signal,
    baseURL: baseURL || undefined,
    headers: { ...headers, ...headersParam },
    responseTypes: 'json',
    requestType: 'json',
  });
};
(getUserByEmailWithPermissions as any).paramsOrder = [{ type: 'parameters' }];

export type getUserAdministratingCompaniesParameters = { userId: IdUserDto };
export type getUserAdministratingCompaniesResponse = PinType<
  ApiResponsePromise<{
    responseCode: 200;
    data: IdCompanyDto[];
    headers: null;
  }>,
  IdCompanyDto[]
>;

export type getUserAdministratingCompaniesSeparateParams = {
  parameters: getUserAdministratingCompaniesParameters;
};
export type getUserAdministratingCompaniesParamsUnion = getUserAdministratingCompaniesParameters;
export type getUserAdministratingCompanies = {
  (arg: {
    params: PinType<getUserAdministratingCompaniesParamsUnion, getUserAdministratingCompaniesSeparateParams>;
    onUploadProgress?: (event: ApiProgressEvent) => void;
    signal?: AbortSignal;
    baseURL?: string;
    isInternal?: boolean;
    headers?: Record<string, string>;
    queryDefaults?: Partial<getUserAdministratingCompaniesParamsUnion> | false;
  }): getUserAdministratingCompaniesResponse;
};

export const getUserAdministratingCompanies: getUserAdministratingCompanies = async ({
  params,
  onUploadProgress,
  signal,
  baseURL,
  isInternal,
  headers: headersParam = {},
  queryDefaults,
  ...otherParams
}) => {
  if (params.userId === undefined) {
    throw new Error('Missing required parameter: userId');
  }

  const path = '/api/ws-permissions/users/{userId}/administrating-companies'.replaceAll('{userId}', () =>
    encodeURIComponent(String(params.userId)),
  );
  const pathInternal = '/api/users/{userId}/administrating-companies'.replaceAll('{userId}', () =>
    encodeURIComponent(String(params.userId)),
  );
  let body: any = {};
  const formData = new FormData();
  const urlParams = new URLSearchParams();

  const headers: Record<string, string> = {};
  headers['Accept'] = 'application/json';

  return makeRequest<ApiResponseArgs<getUserAdministratingCompaniesResponse>>({
    ...otherParams,
    method: 'GET',
    url: isInternal ? pathInternal : path,
    params: urlParams,
    data: {},
    onUploadProgress,
    signal,
    baseURL: baseURL || undefined,
    headers: { ...headers, ...headersParam },
    responseTypes: 'json',
    requestType: 'json',
  });
};
(getUserAdministratingCompanies as any).paramsOrder = [{ type: 'parameters' }];

export type updateAdministratingCompaniesParameters = { userId: IdUserDto };
export type updateAdministratingCompaniesResponse = PinType<
  ApiResponsePromise<{
    responseCode: 200;
    data: void;
    headers: null;
  }>,
  void
>;

export type updateAdministratingCompaniesSeparateParams = {
  body: IdCompanyDto[];
  parameters: updateAdministratingCompaniesParameters;
};
export type updateAdministratingCompaniesParamsUnion = {
  companyIds: IdCompanyDto[];
} & updateAdministratingCompaniesParameters;
export type updateAdministratingCompanies = {
  (arg: {
    params: PinType<updateAdministratingCompaniesParamsUnion, updateAdministratingCompaniesSeparateParams>;
    onUploadProgress?: (event: ApiProgressEvent) => void;
    signal?: AbortSignal;
    baseURL?: string;
    isInternal?: boolean;
    headers?: Record<string, string>;
    queryDefaults?: Partial<updateAdministratingCompaniesParamsUnion> | false;
  }): updateAdministratingCompaniesResponse;
};

export const updateAdministratingCompanies: updateAdministratingCompanies = async ({
  params,
  onUploadProgress,
  signal,
  baseURL,
  isInternal,
  headers: headersParam = {},
  queryDefaults,
  ...otherParams
}) => {
  if (params.userId === undefined) {
    throw new Error('Missing required parameter: userId');
  }
  if (params.companyIds === undefined) {
    throw new Error('Missing required body: companyIds');
  }

  const path = '/api/ws-permissions/users/{userId}/administrating-companies'.replaceAll('{userId}', () =>
    encodeURIComponent(String(params.userId)),
  );
  const pathInternal = '/api/users/{userId}/administrating-companies'.replaceAll('{userId}', () =>
    encodeURIComponent(String(params.userId)),
  );
  let body: any = {};
  const formData = new FormData();
  const urlParams = new URLSearchParams();
  if (params.companyIds !== undefined) {
    body = params.companyIds;
  }

  const headers: Record<string, string> = {};
  headers['Content-Type'] = 'application/json';

  return makeRequest<ApiResponseArgs<updateAdministratingCompaniesResponse>>({
    ...otherParams,
    method: 'PUT',
    url: isInternal ? pathInternal : path,
    params: urlParams,
    data: encodeBody(body),
    onUploadProgress,
    signal,
    baseURL: baseURL || undefined,
    headers: { ...headers, ...headersParam },
    responseTypes: 'void',
    requestType: 'json',
  });
};
(updateAdministratingCompanies as any).paramsOrder = [
  { type: 'body', paramName: 'companyIds' },
  { type: 'parameters' },
];

export type updateCompanyAccountsParameters = { companyId: IdCompanyDto };
export type updateCompanyAccountsResponse = PinType<
  ApiResponsePromise<{
    responseCode: 200;
    data: void;
    headers: null;
  }>,
  void
>;

export type updateCompanyAccountsSeparateParams = {
  body: IdAccountDto[];
  parameters: updateCompanyAccountsParameters;
};
export type updateCompanyAccountsParamsUnion = { accountIds: IdAccountDto[] } & updateCompanyAccountsParameters;
export type updateCompanyAccounts = {
  (arg: {
    params: PinType<updateCompanyAccountsParamsUnion, updateCompanyAccountsSeparateParams>;
    onUploadProgress?: (event: ApiProgressEvent) => void;
    signal?: AbortSignal;
    baseURL?: string;
    isInternal?: boolean;
    headers?: Record<string, string>;
    queryDefaults?: Partial<updateCompanyAccountsParamsUnion> | false;
  }): updateCompanyAccountsResponse;
};

export const updateCompanyAccounts: updateCompanyAccounts = async ({
  params,
  onUploadProgress,
  signal,
  baseURL,
  isInternal,
  headers: headersParam = {},
  queryDefaults,
  ...otherParams
}) => {
  if (params.companyId === undefined) {
    throw new Error('Missing required parameter: companyId');
  }
  if (params.accountIds === undefined) {
    throw new Error('Missing required body: accountIds');
  }

  const path = '/api/ws-permissions/companies/{companyId}/accounts'.replaceAll('{companyId}', () =>
    encodeURIComponent(String(params.companyId)),
  );
  const pathInternal = '/api/companies/{companyId}/accounts'.replaceAll('{companyId}', () =>
    encodeURIComponent(String(params.companyId)),
  );
  let body: any = {};
  const formData = new FormData();
  const urlParams = new URLSearchParams();
  if (params.accountIds !== undefined) {
    body = params.accountIds;
  }

  const headers: Record<string, string> = {};
  headers['Content-Type'] = 'application/json';

  return makeRequest<ApiResponseArgs<updateCompanyAccountsResponse>>({
    ...otherParams,
    method: 'PUT',
    url: isInternal ? pathInternal : path,
    params: urlParams,
    data: encodeBody(body),
    onUploadProgress,
    signal,
    baseURL: baseURL || undefined,
    headers: { ...headers, ...headersParam },
    responseTypes: 'void',
    requestType: 'json',
  });
};
(updateCompanyAccounts as any).paramsOrder = [{ type: 'body', paramName: 'accountIds' }, { type: 'parameters' }];

export type updateCompanyWebsitesParameters = { companyId: IdCompanyDto };
export type updateCompanyWebsitesResponse = PinType<
  ApiResponsePromise<{
    responseCode: 200;
    data: void;
    headers: null;
  }>,
  void
>;

export type updateCompanyWebsitesSeparateParams = {
  body: IdWebsiteDto[];
  parameters: updateCompanyWebsitesParameters;
};
export type updateCompanyWebsitesParamsUnion = { websiteIds: IdWebsiteDto[] } & updateCompanyWebsitesParameters;
export type updateCompanyWebsites = {
  (arg: {
    params: PinType<updateCompanyWebsitesParamsUnion, updateCompanyWebsitesSeparateParams>;
    onUploadProgress?: (event: ApiProgressEvent) => void;
    signal?: AbortSignal;
    baseURL?: string;
    isInternal?: boolean;
    headers?: Record<string, string>;
    queryDefaults?: Partial<updateCompanyWebsitesParamsUnion> | false;
  }): updateCompanyWebsitesResponse;
};

export const updateCompanyWebsites: updateCompanyWebsites = async ({
  params,
  onUploadProgress,
  signal,
  baseURL,
  isInternal,
  headers: headersParam = {},
  queryDefaults,
  ...otherParams
}) => {
  if (params.companyId === undefined) {
    throw new Error('Missing required parameter: companyId');
  }
  if (params.websiteIds === undefined) {
    throw new Error('Missing required body: websiteIds');
  }

  const path = '/api/ws-permissions/companies/{companyId}/websites'.replaceAll('{companyId}', () =>
    encodeURIComponent(String(params.companyId)),
  );
  const pathInternal = '/api/companies/{companyId}/websites'.replaceAll('{companyId}', () =>
    encodeURIComponent(String(params.companyId)),
  );
  let body: any = {};
  const formData = new FormData();
  const urlParams = new URLSearchParams();
  if (params.websiteIds !== undefined) {
    body = params.websiteIds;
  }

  const headers: Record<string, string> = {};
  headers['Content-Type'] = 'application/json';

  return makeRequest<ApiResponseArgs<updateCompanyWebsitesResponse>>({
    ...otherParams,
    method: 'PUT',
    url: isInternal ? pathInternal : path,
    params: urlParams,
    data: encodeBody(body),
    onUploadProgress,
    signal,
    baseURL: baseURL || undefined,
    headers: { ...headers, ...headersParam },
    responseTypes: 'void',
    requestType: 'json',
  });
};
(updateCompanyWebsites as any).paramsOrder = [{ type: 'body', paramName: 'websiteIds' }, { type: 'parameters' }];

export type getCompanyPermissionsParameters = { companyId: IdCompanyDto };
export type getCompanyPermissionsResponse = PinType<
  ApiResponsePromise<{
    responseCode: 200;
    data: Types.CompanyPermissionsDto;
    headers: null;
  }>,
  Types.CompanyPermissionsDto
>;

export type getCompanyPermissionsSeparateParams = {
  parameters: getCompanyPermissionsParameters;
};
export type getCompanyPermissionsParamsUnion = getCompanyPermissionsParameters;
export type getCompanyPermissions = {
  (arg: {
    params: PinType<getCompanyPermissionsParamsUnion, getCompanyPermissionsSeparateParams>;
    onUploadProgress?: (event: ApiProgressEvent) => void;
    signal?: AbortSignal;
    baseURL?: string;
    isInternal?: boolean;
    headers?: Record<string, string>;
    queryDefaults?: Partial<getCompanyPermissionsParamsUnion> | false;
  }): getCompanyPermissionsResponse;
};

export const getCompanyPermissions: getCompanyPermissions = async ({
  params,
  onUploadProgress,
  signal,
  baseURL,
  isInternal,
  headers: headersParam = {},
  queryDefaults,
  ...otherParams
}) => {
  if (params.companyId === undefined) {
    throw new Error('Missing required parameter: companyId');
  }

  const path = '/api/ws-permissions/companies/{companyId}/permissions'.replaceAll('{companyId}', () =>
    encodeURIComponent(String(params.companyId)),
  );
  const pathInternal = '/api/companies/{companyId}/permissions'.replaceAll('{companyId}', () =>
    encodeURIComponent(String(params.companyId)),
  );
  let body: any = {};
  const formData = new FormData();
  const urlParams = new URLSearchParams();

  const headers: Record<string, string> = {};
  headers['Accept'] = 'application/json';

  return makeRequest<ApiResponseArgs<getCompanyPermissionsResponse>>({
    ...otherParams,
    method: 'GET',
    url: isInternal ? pathInternal : path,
    params: urlParams,
    data: {},
    onUploadProgress,
    signal,
    baseURL: baseURL || undefined,
    headers: { ...headers, ...headersParam },
    responseTypes: 'json',
    requestType: 'json',
  });
};
(getCompanyPermissions as any).paramsOrder = [{ type: 'parameters' }];

export type updateCompanyPermissionsParameters = { companyId: IdCompanyDto };
export type updateCompanyPermissionsResponse = PinType<
  ApiResponsePromise<{
    responseCode: 200;
    data: void;
    headers: null;
  }>,
  void
>;

export type updateCompanyPermissionsSeparateParams = {
  body: Types.CompanyPermissionsUpdateForm;
  parameters: updateCompanyPermissionsParameters;
};
export type updateCompanyPermissionsParamsUnion = {
  form: Types.CompanyPermissionsUpdateForm;
} & updateCompanyPermissionsParameters;
export type updateCompanyPermissions = {
  (arg: {
    params: PinType<updateCompanyPermissionsParamsUnion, updateCompanyPermissionsSeparateParams>;
    onUploadProgress?: (event: ApiProgressEvent) => void;
    signal?: AbortSignal;
    baseURL?: string;
    isInternal?: boolean;
    headers?: Record<string, string>;
    queryDefaults?: Partial<updateCompanyPermissionsParamsUnion> | false;
  }): updateCompanyPermissionsResponse;
};

export const updateCompanyPermissions: updateCompanyPermissions = async ({
  params,
  onUploadProgress,
  signal,
  baseURL,
  isInternal,
  headers: headersParam = {},
  queryDefaults,
  ...otherParams
}) => {
  if (params.companyId === undefined) {
    throw new Error('Missing required parameter: companyId');
  }
  if (params.form === undefined) {
    throw new Error('Missing required body: form');
  }

  const path = '/api/ws-permissions/companies/{companyId}/permissions'.replaceAll('{companyId}', () =>
    encodeURIComponent(String(params.companyId)),
  );
  const pathInternal = '/api/companies/{companyId}/permissions'.replaceAll('{companyId}', () =>
    encodeURIComponent(String(params.companyId)),
  );
  let body: any = {};
  const formData = new FormData();
  const urlParams = new URLSearchParams();
  if (params.form !== undefined) {
    body = params.form;
  }

  const headers: Record<string, string> = {};
  headers['Content-Type'] = 'application/json';

  return makeRequest<ApiResponseArgs<updateCompanyPermissionsResponse>>({
    ...otherParams,
    method: 'PUT',
    url: isInternal ? pathInternal : path,
    params: urlParams,
    data: encodeBody(body),
    onUploadProgress,
    signal,
    baseURL: baseURL || undefined,
    headers: { ...headers, ...headersParam },
    responseTypes: 'void',
    requestType: 'json',
  });
};
(updateCompanyPermissions as any).paramsOrder = [{ type: 'body', paramName: 'form' }, { type: 'parameters' }];

export type getCompaniesPermissionsParameters = { /** List of company IDs */ companyIds: IdCompanyDto[] };
export type getCompaniesPermissionsResponse = PinType<
  ApiResponsePromise<{
    responseCode: 200;
    data: Types.CompanyPermissionsDto[];
    headers: null;
  }>,
  Types.CompanyPermissionsDto[]
>;

export type getCompaniesPermissionsSeparateParams = {
  parameters: getCompaniesPermissionsParameters;
};
export type getCompaniesPermissionsParamsUnion = getCompaniesPermissionsParameters;
export type getCompaniesPermissions = {
  (arg: {
    params: PinType<getCompaniesPermissionsParamsUnion, getCompaniesPermissionsSeparateParams>;
    onUploadProgress?: (event: ApiProgressEvent) => void;
    signal?: AbortSignal;
    baseURL?: string;
    isInternal?: boolean;
    headers?: Record<string, string>;
    queryDefaults?: Partial<getCompaniesPermissionsParamsUnion> | false;
  }): getCompaniesPermissionsResponse;
};

export const getCompaniesPermissions: getCompaniesPermissions = async ({
  params,
  onUploadProgress,
  signal,
  baseURL,
  isInternal,
  headers: headersParam = {},
  queryDefaults,
  ...otherParams
}) => {
  if (params.companyIds === undefined) {
    throw new Error('Missing required parameter: companyIds');
  }

  const path = '/api/ws-permissions/companies/permissions/by-ids';
  const pathInternal = '/api/companies/permissions/by-ids';
  let body: any = {};
  const formData = new FormData();
  const urlParams = new URLSearchParams();
  if (params.companyIds !== undefined) {
    urlParams.append('companyIds', params.companyIds.toString());
  }

  const headers: Record<string, string> = {};
  headers['Accept'] = 'application/json';

  return makeRequest<ApiResponseArgs<getCompaniesPermissionsResponse>>({
    ...otherParams,
    method: 'GET',
    url: isInternal ? pathInternal : path,
    params: urlParams,
    data: {},
    onUploadProgress,
    signal,
    baseURL: baseURL || undefined,
    headers: { ...headers, ...headersParam },
    responseTypes: 'json',
    requestType: 'json',
  });
};
(getCompaniesPermissions as any).paramsOrder = [{ type: 'parameters' }];

export type getCompanyPermissionsIncludeInactiveParameters = { companyId: IdCompanyDto };
export type getCompanyPermissionsIncludeInactiveResponse = PinType<
  ApiResponsePromise<{
    responseCode: 200;
    data: Types.CompanyPermissionsDto;
    headers: null;
  }>,
  Types.CompanyPermissionsDto
>;

export type getCompanyPermissionsIncludeInactiveSeparateParams = {
  parameters: getCompanyPermissionsIncludeInactiveParameters;
};
export type getCompanyPermissionsIncludeInactiveParamsUnion = getCompanyPermissionsIncludeInactiveParameters;
export type getCompanyPermissionsIncludeInactive = {
  (arg: {
    params: PinType<
      getCompanyPermissionsIncludeInactiveParamsUnion,
      getCompanyPermissionsIncludeInactiveSeparateParams
    >;
    onUploadProgress?: (event: ApiProgressEvent) => void;
    signal?: AbortSignal;
    baseURL?: string;
    isInternal?: boolean;
    headers?: Record<string, string>;
    queryDefaults?: Partial<getCompanyPermissionsIncludeInactiveParamsUnion> | false;
  }): getCompanyPermissionsIncludeInactiveResponse;
};

export const getCompanyPermissionsIncludeInactive: getCompanyPermissionsIncludeInactive = async ({
  params,
  onUploadProgress,
  signal,
  baseURL,
  isInternal,
  headers: headersParam = {},
  queryDefaults,
  ...otherParams
}) => {
  if (params.companyId === undefined) {
    throw new Error('Missing required parameter: companyId');
  }

  const path = '/api/ws-permissions/companies/{companyId}/permissions/include-inactive'.replaceAll('{companyId}', () =>
    encodeURIComponent(String(params.companyId)),
  );
  const pathInternal = '/api/companies/{companyId}/permissions/include-inactive'.replaceAll('{companyId}', () =>
    encodeURIComponent(String(params.companyId)),
  );
  let body: any = {};
  const formData = new FormData();
  const urlParams = new URLSearchParams();

  const headers: Record<string, string> = {};
  headers['Accept'] = 'application/json';

  return makeRequest<ApiResponseArgs<getCompanyPermissionsIncludeInactiveResponse>>({
    ...otherParams,
    method: 'GET',
    url: isInternal ? pathInternal : path,
    params: urlParams,
    data: {},
    onUploadProgress,
    signal,
    baseURL: baseURL || undefined,
    headers: { ...headers, ...headersParam },
    responseTypes: 'json',
    requestType: 'json',
  });
};
(getCompanyPermissionsIncludeInactive as any).paramsOrder = [{ type: 'parameters' }];

export type hasCompanyPermissionParameters = { companyId: IdCompanyDto; permission: string };
export type hasCompanyPermissionResponse = PinType<
  ApiResponsePromise<{
    responseCode: 200;
    data: boolean;
    headers: null;
  }>,
  boolean
>;

export type hasCompanyPermissionSeparateParams = {
  parameters: hasCompanyPermissionParameters;
};
export type hasCompanyPermissionParamsUnion = hasCompanyPermissionParameters;
export type hasCompanyPermission = {
  (arg: {
    params: PinType<hasCompanyPermissionParamsUnion, hasCompanyPermissionSeparateParams>;
    onUploadProgress?: (event: ApiProgressEvent) => void;
    signal?: AbortSignal;
    baseURL?: string;
    isInternal?: boolean;
    headers?: Record<string, string>;
    queryDefaults?: Partial<hasCompanyPermissionParamsUnion> | false;
  }): hasCompanyPermissionResponse;
};

export const hasCompanyPermission: hasCompanyPermission = async ({
  params,
  onUploadProgress,
  signal,
  baseURL,
  isInternal,
  headers: headersParam = {},
  queryDefaults,
  ...otherParams
}) => {
  if (params.companyId === undefined) {
    throw new Error('Missing required parameter: companyId');
  }
  if (params.permission === undefined) {
    throw new Error('Missing required parameter: permission');
  }

  const path = '/api/ws-permissions/companies/{companyId}/permissions/check'.replaceAll('{companyId}', () =>
    encodeURIComponent(String(params.companyId)),
  );
  const pathInternal = '/api/companies/{companyId}/permissions/check'.replaceAll('{companyId}', () =>
    encodeURIComponent(String(params.companyId)),
  );
  let body: any = {};
  const formData = new FormData();
  const urlParams = new URLSearchParams();
  if (params.permission !== undefined) {
    urlParams.append('permission', params.permission.toString());
  }

  const headers: Record<string, string> = {};
  headers['Accept'] = 'application/json';

  return makeRequest<ApiResponseArgs<hasCompanyPermissionResponse>>({
    ...otherParams,
    method: 'GET',
    url: isInternal ? pathInternal : path,
    params: urlParams,
    data: {},
    onUploadProgress,
    signal,
    baseURL: baseURL || undefined,
    headers: { ...headers, ...headersParam },
    responseTypes: 'json',
    requestType: 'json',
  });
};
(hasCompanyPermission as any).paramsOrder = [{ type: 'parameters' }];

export type hasCompanyPermissionByUUIDParameters = { companyUUID: string; permission: string };
export type hasCompanyPermissionByUUIDResponse = PinType<
  ApiResponsePromise<{
    responseCode: 200;
    data: boolean;
    headers: null;
  }>,
  boolean
>;

export type hasCompanyPermissionByUUIDSeparateParams = {
  parameters: hasCompanyPermissionByUUIDParameters;
};
export type hasCompanyPermissionByUUIDParamsUnion = hasCompanyPermissionByUUIDParameters;
export type hasCompanyPermissionByUUID = {
  (arg: {
    params: PinType<hasCompanyPermissionByUUIDParamsUnion, hasCompanyPermissionByUUIDSeparateParams>;
    onUploadProgress?: (event: ApiProgressEvent) => void;
    signal?: AbortSignal;
    baseURL?: string;
    isInternal?: boolean;
    headers?: Record<string, string>;
    queryDefaults?: Partial<hasCompanyPermissionByUUIDParamsUnion> | false;
  }): hasCompanyPermissionByUUIDResponse;
};

export const hasCompanyPermissionByUUID: hasCompanyPermissionByUUID = async ({
  params,
  onUploadProgress,
  signal,
  baseURL,
  isInternal,
  headers: headersParam = {},
  queryDefaults,
  ...otherParams
}) => {
  if (params.companyUUID === undefined) {
    throw new Error('Missing required parameter: companyUUID');
  }
  if (params.permission === undefined) {
    throw new Error('Missing required parameter: permission');
  }

  const path = '/api/ws-permissions/companies/{companyUUID}/permissions/check-by-uuid'.replaceAll('{companyUUID}', () =>
    encodeURIComponent(String(params.companyUUID)),
  );
  const pathInternal = '/api/companies/{companyUUID}/permissions/check-by-uuid'.replaceAll('{companyUUID}', () =>
    encodeURIComponent(String(params.companyUUID)),
  );
  let body: any = {};
  const formData = new FormData();
  const urlParams = new URLSearchParams();
  if (params.permission !== undefined) {
    urlParams.append('permission', params.permission.toString());
  }

  const headers: Record<string, string> = {};
  headers['Accept'] = 'application/json';

  return makeRequest<ApiResponseArgs<hasCompanyPermissionByUUIDResponse>>({
    ...otherParams,
    method: 'GET',
    url: isInternal ? pathInternal : path,
    params: urlParams,
    data: {},
    onUploadProgress,
    signal,
    baseURL: baseURL || undefined,
    headers: { ...headers, ...headersParam },
    responseTypes: 'json',
    requestType: 'json',
  });
};
(hasCompanyPermissionByUUID as any).paramsOrder = [{ type: 'parameters' }];

export type getAllManagingCompaniesIdsParameters = {
  companyId: IdCompanyDto;
  /** List of permissions */ permissions: string[];
};
export type getAllManagingCompaniesIdsResponse = PinType<
  ApiResponsePromise<{
    responseCode: 200;
    data: number[];
    headers: null;
  }>,
  number[]
>;

export type getAllManagingCompaniesIdsSeparateParams = {
  parameters: getAllManagingCompaniesIdsParameters;
};
export type getAllManagingCompaniesIdsParamsUnion = getAllManagingCompaniesIdsParameters;
export type getAllManagingCompaniesIds = {
  (arg: {
    params: PinType<getAllManagingCompaniesIdsParamsUnion, getAllManagingCompaniesIdsSeparateParams>;
    onUploadProgress?: (event: ApiProgressEvent) => void;
    signal?: AbortSignal;
    baseURL?: string;
    isInternal?: boolean;
    headers?: Record<string, string>;
    queryDefaults?: Partial<getAllManagingCompaniesIdsParamsUnion> | false;
  }): getAllManagingCompaniesIdsResponse;
};

export const getAllManagingCompaniesIds: getAllManagingCompaniesIds = async ({
  params,
  onUploadProgress,
  signal,
  baseURL,
  isInternal,
  headers: headersParam = {},
  queryDefaults,
  ...otherParams
}) => {
  if (params.companyId === undefined) {
    throw new Error('Missing required parameter: companyId');
  }
  if (params.permissions === undefined) {
    throw new Error('Missing required parameter: permissions');
  }

  const path = '/api/ws-permissions/companies/{companyId}/managing-companies-ids'.replaceAll('{companyId}', () =>
    encodeURIComponent(String(params.companyId)),
  );
  const pathInternal = '/api/companies/{companyId}/managing-companies-ids'.replaceAll('{companyId}', () =>
    encodeURIComponent(String(params.companyId)),
  );
  let body: any = {};
  const formData = new FormData();
  const urlParams = new URLSearchParams();
  if (params.permissions !== undefined) {
    urlParams.append('permissions', params.permissions.toString());
  }

  const headers: Record<string, string> = {};
  headers['Accept'] = 'application/json';

  return makeRequest<ApiResponseArgs<getAllManagingCompaniesIdsResponse>>({
    ...otherParams,
    method: 'GET',
    url: isInternal ? pathInternal : path,
    params: urlParams,
    data: {},
    onUploadProgress,
    signal,
    baseURL: baseURL || undefined,
    headers: { ...headers, ...headersParam },
    responseTypes: 'json',
    requestType: 'json',
  });
};
(getAllManagingCompaniesIds as any).paramsOrder = [{ type: 'parameters' }];

export type getAllManagingCompaniesNamesParameters = {
  id: IdCompanyDto;
  /** List of permissions */ permissions: string[];
};
export type getAllManagingCompaniesNamesResponse = PinType<
  ApiResponsePromise<{
    responseCode: 200;
    data: Types.CompanyDto[];
    headers: null;
  }>,
  Types.CompanyDto[]
>;

export type getAllManagingCompaniesNamesSeparateParams = {
  parameters: getAllManagingCompaniesNamesParameters;
};
export type getAllManagingCompaniesNamesParamsUnion = getAllManagingCompaniesNamesParameters;
export type getAllManagingCompaniesNames = {
  (arg: {
    params: PinType<getAllManagingCompaniesNamesParamsUnion, getAllManagingCompaniesNamesSeparateParams>;
    onUploadProgress?: (event: ApiProgressEvent) => void;
    signal?: AbortSignal;
    baseURL?: string;
    isInternal?: boolean;
    headers?: Record<string, string>;
    queryDefaults?: Partial<getAllManagingCompaniesNamesParamsUnion> | false;
  }): getAllManagingCompaniesNamesResponse;
};

export const getAllManagingCompaniesNames: getAllManagingCompaniesNames = async ({
  params,
  onUploadProgress,
  signal,
  baseURL,
  isInternal,
  headers: headersParam = {},
  queryDefaults,
  ...otherParams
}) => {
  if (params.id === undefined) {
    throw new Error('Missing required parameter: id');
  }
  if (params.permissions === undefined) {
    throw new Error('Missing required parameter: permissions');
  }

  const path = '/api/ws-permissions/companies/{id}/managing-companies-names'.replaceAll('{id}', () =>
    encodeURIComponent(String(params.id)),
  );
  const pathInternal = '/api/companies/{id}/managing-companies-names'.replaceAll('{id}', () =>
    encodeURIComponent(String(params.id)),
  );
  let body: any = {};
  const formData = new FormData();
  const urlParams = new URLSearchParams();
  if (params.permissions !== undefined) {
    urlParams.append('permissions', params.permissions.toString());
  }

  const headers: Record<string, string> = {};
  headers['Accept'] = 'application/json';
  headers['Content-Type'] = 'application/json';

  return makeRequest<ApiResponseArgs<getAllManagingCompaniesNamesResponse>>({
    ...otherParams,
    method: 'GET',
    url: isInternal ? pathInternal : path,
    params: urlParams,
    data: {},
    onUploadProgress,
    signal,
    baseURL: baseURL || undefined,
    headers: { ...headers, ...headersParam },
    responseTypes: 'json',
    requestType: 'json',
  });
};
(getAllManagingCompaniesNames as any).paramsOrder = [{ type: 'parameters' }];

export type getManagedByCompanyParameters = { companyId: IdCompanyDto; permission: string };
export type getManagedByCompanyResponse = PinType<
  ApiResponsePromise<{
    responseCode: 200;
    data: Types.CompanyDto;
    headers: null;
  }>,
  Types.CompanyDto
>;

export type getManagedByCompanySeparateParams = {
  parameters: getManagedByCompanyParameters;
};
export type getManagedByCompanyParamsUnion = getManagedByCompanyParameters;
export type getManagedByCompany = {
  (arg: {
    params: PinType<getManagedByCompanyParamsUnion, getManagedByCompanySeparateParams>;
    onUploadProgress?: (event: ApiProgressEvent) => void;
    signal?: AbortSignal;
    baseURL?: string;
    isInternal?: boolean;
    headers?: Record<string, string>;
    queryDefaults?: Partial<getManagedByCompanyParamsUnion> | false;
  }): getManagedByCompanyResponse;
};

export const getManagedByCompany: getManagedByCompany = async ({
  params,
  onUploadProgress,
  signal,
  baseURL,
  isInternal,
  headers: headersParam = {},
  queryDefaults,
  ...otherParams
}) => {
  if (params.companyId === undefined) {
    throw new Error('Missing required parameter: companyId');
  }
  if (params.permission === undefined) {
    throw new Error('Missing required parameter: permission');
  }

  const path = '/api/ws-permissions/companies/{companyId}/permissions/managed-by'.replaceAll('{companyId}', () =>
    encodeURIComponent(String(params.companyId)),
  );
  const pathInternal = '/api/companies/{companyId}/permissions/managed-by'.replaceAll('{companyId}', () =>
    encodeURIComponent(String(params.companyId)),
  );
  let body: any = {};
  const formData = new FormData();
  const urlParams = new URLSearchParams();
  if (params.permission !== undefined) {
    urlParams.append('permission', params.permission.toString());
  }

  const headers: Record<string, string> = {};
  headers['Accept'] = 'application/json';

  return makeRequest<ApiResponseArgs<getManagedByCompanyResponse>>({
    ...otherParams,
    method: 'GET',
    url: isInternal ? pathInternal : path,
    params: urlParams,
    data: {},
    onUploadProgress,
    signal,
    baseURL: baseURL || undefined,
    headers: { ...headers, ...headersParam },
    responseTypes: 'json',
    requestType: 'json',
  });
};
(getManagedByCompany as any).paramsOrder = [{ type: 'parameters' }];

export type getCompanyPermissionsSummaryParameters = { companyId: IdCompanyDto; userId: string };
export type getCompanyPermissionsSummaryResponse = PinType<
  ApiResponsePromise<{
    responseCode: 200;
    data: Types.CompanyPermissionsSummaryDto;
    headers: null;
  }>,
  Types.CompanyPermissionsSummaryDto
>;

export type getCompanyPermissionsSummarySeparateParams = {
  parameters: getCompanyPermissionsSummaryParameters;
};
export type getCompanyPermissionsSummaryParamsUnion = getCompanyPermissionsSummaryParameters;
export type getCompanyPermissionsSummary = {
  (arg: {
    params: PinType<getCompanyPermissionsSummaryParamsUnion, getCompanyPermissionsSummarySeparateParams>;
    onUploadProgress?: (event: ApiProgressEvent) => void;
    signal?: AbortSignal;
    baseURL?: string;
    isInternal?: boolean;
    headers?: Record<string, string>;
    queryDefaults?: Partial<getCompanyPermissionsSummaryParamsUnion> | false;
  }): getCompanyPermissionsSummaryResponse;
};

export const getCompanyPermissionsSummary: getCompanyPermissionsSummary = async ({
  params,
  onUploadProgress,
  signal,
  baseURL,
  isInternal,
  headers: headersParam = {},
  queryDefaults,
  ...otherParams
}) => {
  if (params.companyId === undefined) {
    throw new Error('Missing required parameter: companyId');
  }
  if (params.userId === undefined) {
    throw new Error('Missing required parameter: userId');
  }

  const path = '/api/ws-permissions/companies/{companyId}/permissions/summary'.replaceAll('{companyId}', () =>
    encodeURIComponent(String(params.companyId)),
  );
  const pathInternal = '/api/companies/{companyId}/permissions/summary'.replaceAll('{companyId}', () =>
    encodeURIComponent(String(params.companyId)),
  );
  let body: any = {};
  const formData = new FormData();
  const urlParams = new URLSearchParams();
  if (params.userId !== undefined) {
    urlParams.append('userId', params.userId.toString());
  }

  const headers: Record<string, string> = {};
  headers['Accept'] = 'application/json';

  return makeRequest<ApiResponseArgs<getCompanyPermissionsSummaryResponse>>({
    ...otherParams,
    method: 'GET',
    url: isInternal ? pathInternal : path,
    params: urlParams,
    data: {},
    onUploadProgress,
    signal,
    baseURL: baseURL || undefined,
    headers: { ...headers, ...headersParam },
    responseTypes: 'json',
    requestType: 'json',
  });
};
(getCompanyPermissionsSummary as any).paramsOrder = [{ type: 'parameters' }];

export type getUserHeaderHashParameters = { companyId: IdCompanyDto; userId: string };
export type getUserHeaderHashResponse = PinType<
  ApiResponsePromise<{
    responseCode: 200;
    data: string;
    headers: null;
  }>,
  string
>;

export type getUserHeaderHashSeparateParams = {
  parameters: getUserHeaderHashParameters;
};
export type getUserHeaderHashParamsUnion = getUserHeaderHashParameters;
export type getUserHeaderHash = {
  (arg: {
    params: PinType<getUserHeaderHashParamsUnion, getUserHeaderHashSeparateParams>;
    onUploadProgress?: (event: ApiProgressEvent) => void;
    signal?: AbortSignal;
    baseURL?: string;
    isInternal?: boolean;
    headers?: Record<string, string>;
    queryDefaults?: Partial<getUserHeaderHashParamsUnion> | false;
  }): getUserHeaderHashResponse;
};

export const getUserHeaderHash: getUserHeaderHash = async ({
  params,
  onUploadProgress,
  signal,
  baseURL,
  isInternal,
  headers: headersParam = {},
  queryDefaults,
  ...otherParams
}) => {
  if (params.companyId === undefined) {
    throw new Error('Missing required parameter: companyId');
  }
  if (params.userId === undefined) {
    throw new Error('Missing required parameter: userId');
  }

  const path = '/api/ws-permissions/companies/{companyId}/permissions/summary/hash'.replaceAll('{companyId}', () =>
    encodeURIComponent(String(params.companyId)),
  );
  const pathInternal = '/api/companies/{companyId}/permissions/summary/hash'.replaceAll('{companyId}', () =>
    encodeURIComponent(String(params.companyId)),
  );
  let body: any = {};
  const formData = new FormData();
  const urlParams = new URLSearchParams();
  if (params.userId !== undefined) {
    urlParams.append('userId', params.userId.toString());
  }

  const headers: Record<string, string> = {};
  headers['Accept'] = 'application/json';

  return makeRequest<ApiResponseArgs<getUserHeaderHashResponse>>({
    ...otherParams,
    method: 'GET',
    url: isInternal ? pathInternal : path,
    params: urlParams,
    data: {},
    onUploadProgress,
    signal,
    baseURL: baseURL || undefined,
    headers: { ...headers, ...headersParam },
    responseTypes: 'json',
    requestType: 'json',
  });
};
(getUserHeaderHash as any).paramsOrder = [{ type: 'parameters' }];

export type getPermittedWebsiteCompanyIdsParameters = { websiteId: IdWebsiteDto };
export type getPermittedWebsiteCompanyIdsResponse = PinType<
  ApiResponsePromise<{
    responseCode: 200;
    data: IdCompanyDto[];
    headers: null;
  }>,
  IdCompanyDto[]
>;

export type getPermittedWebsiteCompanyIdsSeparateParams = {
  parameters: getPermittedWebsiteCompanyIdsParameters;
};
export type getPermittedWebsiteCompanyIdsParamsUnion = getPermittedWebsiteCompanyIdsParameters;
export type getPermittedWebsiteCompanyIds = {
  (arg: {
    params: PinType<getPermittedWebsiteCompanyIdsParamsUnion, getPermittedWebsiteCompanyIdsSeparateParams>;
    onUploadProgress?: (event: ApiProgressEvent) => void;
    signal?: AbortSignal;
    baseURL?: string;
    isInternal?: boolean;
    headers?: Record<string, string>;
    queryDefaults?: Partial<getPermittedWebsiteCompanyIdsParamsUnion> | false;
  }): getPermittedWebsiteCompanyIdsResponse;
};

export const getPermittedWebsiteCompanyIds: getPermittedWebsiteCompanyIds = async ({
  params,
  onUploadProgress,
  signal,
  baseURL,
  isInternal,
  headers: headersParam = {},
  queryDefaults,
  ...otherParams
}) => {
  if (params.websiteId === undefined) {
    throw new Error('Missing required parameter: websiteId');
  }

  const path = '/api/ws-permissions/websites/{websiteId}/company-ids'.replaceAll('{websiteId}', () =>
    encodeURIComponent(String(params.websiteId)),
  );
  const pathInternal = '/api/websites/{websiteId}/company-ids'.replaceAll('{websiteId}', () =>
    encodeURIComponent(String(params.websiteId)),
  );
  let body: any = {};
  const formData = new FormData();
  const urlParams = new URLSearchParams();

  const headers: Record<string, string> = {};
  headers['Accept'] = 'application/json';

  return makeRequest<ApiResponseArgs<getPermittedWebsiteCompanyIdsResponse>>({
    ...otherParams,
    method: 'GET',
    url: isInternal ? pathInternal : path,
    params: urlParams,
    data: {},
    onUploadProgress,
    signal,
    baseURL: baseURL || undefined,
    headers: { ...headers, ...headersParam },
    responseTypes: 'json',
    requestType: 'json',
  });
};
(getPermittedWebsiteCompanyIds as any).paramsOrder = [{ type: 'parameters' }];

export type getPaginatedCompaniesNamesParameters = {
  filter?: string;
  /** zero-based page index */ page?: number;
  /** the size of the page to be returned */ size?: number;
  /** sort direction */ sortDirection?: string;
  /** comma separated list of sort properties */ sortProperties?: string;
};
export type getPaginatedCompaniesNamesResponse = PinType<
  ApiResponsePromise<{
    responseCode: 200;
    data: Types.PaginatedCompaniesNames;
    headers: null;
  }>,
  Types.PaginatedCompaniesNames
>;

export type getPaginatedCompaniesNamesSeparateParams = {
  parameters: getPaginatedCompaniesNamesParameters;
};
export type getPaginatedCompaniesNamesParamsUnion = getPaginatedCompaniesNamesParameters;
export type getPaginatedCompaniesNames = {
  (arg: {
    params: PinType<getPaginatedCompaniesNamesParamsUnion, getPaginatedCompaniesNamesSeparateParams>;
    onUploadProgress?: (event: ApiProgressEvent) => void;
    signal?: AbortSignal;
    baseURL?: string;
    isInternal?: boolean;
    headers?: Record<string, string>;
    queryDefaults?: Partial<getPaginatedCompaniesNamesParamsUnion> | false;
  }): getPaginatedCompaniesNamesResponse;
};

export const getPaginatedCompaniesNames: getPaginatedCompaniesNames = async ({
  params,
  onUploadProgress,
  signal,
  baseURL,
  isInternal,
  headers: headersParam = {},
  queryDefaults,
  ...otherParams
}) => {
  const path = '/api/ws-permissions/company/names/paginated';
  const pathInternal = '/api/company/names/paginated';
  let body: any = {};
  const formData = new FormData();
  const urlParams = new URLSearchParams();
  {
    const value = queryDefaults === false ? params.page : (params.page ?? queryDefaults?.['page']);
    if (value !== undefined) {
      urlParams.append('page', value.toString());
    }
  }
  {
    const value = queryDefaults === false ? params.size : (params.size ?? queryDefaults?.['size']);
    if (value !== undefined) {
      urlParams.append('size', value.toString());
    }
  }
  {
    const value =
      queryDefaults === false ? params.sortDirection : (params.sortDirection ?? queryDefaults?.['sortDirection']);
    if (value !== undefined) {
      urlParams.append('sortDirection', value.toString());
    }
  }
  {
    const value =
      queryDefaults === false ? params.sortProperties : (params.sortProperties ?? queryDefaults?.['sortProperties']);
    if (value !== undefined) {
      urlParams.append('sortProperties', value.toString());
    }
  }
  {
    const value = queryDefaults === false ? params.filter : (params.filter ?? queryDefaults?.['filter']);
    if (value !== undefined) {
      urlParams.append('filter', value.toString());
    }
  }

  const headers: Record<string, string> = {};
  headers['Accept'] = 'application/json';

  return makeRequest<ApiResponseArgs<getPaginatedCompaniesNamesResponse>>({
    ...otherParams,
    method: 'GET',
    url: isInternal ? pathInternal : path,
    params: urlParams,
    data: {},
    onUploadProgress,
    signal,
    baseURL: baseURL || undefined,
    headers: { ...headers, ...headersParam },
    responseTypes: 'json',
    requestType: 'json',
  });
};
(getPaginatedCompaniesNames as any).paramsOrder = [{ type: 'parameters' }];

export type getPermissionTypesResponse = PinType<
  ApiResponsePromise<{
    responseCode: 200;
    data: Permissions.PermissionType[];
    headers: null;
  }>,
  Permissions.PermissionType[]
>;

export type getPermissionTypesSeparateParams = {};
export type getPermissionTypesParamsUnion = Record<string, never>;
export type getPermissionTypes = {
  (arg: {
    onUploadProgress?: (event: ApiProgressEvent) => void;
    signal?: AbortSignal;
    baseURL?: string;
    isInternal?: boolean;
    headers?: Record<string, string>;
    queryDefaults?: Partial<getPermissionTypesParamsUnion> | false;
  }): getPermissionTypesResponse;
};

export const getPermissionTypes: getPermissionTypes = async ({
  onUploadProgress,
  signal,
  baseURL,
  isInternal,
  headers: headersParam = {},
  queryDefaults,
  ...otherParams
}) => {
  const path = '/api/ws-permissions/permission-types';
  const pathInternal = '/api/permission-types';
  let body: any = {};
  const formData = new FormData();
  const urlParams = new URLSearchParams();

  const headers: Record<string, string> = {};
  headers['Accept'] = 'application/json';

  return makeRequest<ApiResponseArgs<getPermissionTypesResponse>>({
    ...otherParams,
    method: 'GET',
    url: isInternal ? pathInternal : path,
    params: urlParams,
    data: {},
    onUploadProgress,
    signal,
    baseURL: baseURL || undefined,
    headers: { ...headers, ...headersParam },
    responseTypes: 'json',
    requestType: 'json',
  });
};
(getPermissionTypes as any).paramsOrder = [];

export type getCompanyPermissionTypesParameters = { companyId: IdCompanyDto };
export type getCompanyPermissionTypesResponse = PinType<
  ApiResponsePromise<{
    responseCode: 200;
    data: Permissions.PermissionType[];
    headers: null;
  }>,
  Permissions.PermissionType[]
>;

export type getCompanyPermissionTypesSeparateParams = {
  parameters: getCompanyPermissionTypesParameters;
};
export type getCompanyPermissionTypesParamsUnion = getCompanyPermissionTypesParameters;
export type getCompanyPermissionTypes = {
  (arg: {
    params: PinType<getCompanyPermissionTypesParamsUnion, getCompanyPermissionTypesSeparateParams>;
    onUploadProgress?: (event: ApiProgressEvent) => void;
    signal?: AbortSignal;
    baseURL?: string;
    isInternal?: boolean;
    headers?: Record<string, string>;
    queryDefaults?: Partial<getCompanyPermissionTypesParamsUnion> | false;
  }): getCompanyPermissionTypesResponse;
};

export const getCompanyPermissionTypes: getCompanyPermissionTypes = async ({
  params,
  onUploadProgress,
  signal,
  baseURL,
  isInternal,
  headers: headersParam = {},
  queryDefaults,
  ...otherParams
}) => {
  if (params.companyId === undefined) {
    throw new Error('Missing required parameter: companyId');
  }

  const path = '/api/ws-permissions/companies/{companyId}/permission-types'.replaceAll('{companyId}', () =>
    encodeURIComponent(String(params.companyId)),
  );
  const pathInternal = '/api/companies/{companyId}/permission-types'.replaceAll('{companyId}', () =>
    encodeURIComponent(String(params.companyId)),
  );
  let body: any = {};
  const formData = new FormData();
  const urlParams = new URLSearchParams();

  const headers: Record<string, string> = {};
  headers['Accept'] = 'application/json';

  return makeRequest<ApiResponseArgs<getCompanyPermissionTypesResponse>>({
    ...otherParams,
    method: 'GET',
    url: isInternal ? pathInternal : path,
    params: urlParams,
    data: {},
    onUploadProgress,
    signal,
    baseURL: baseURL || undefined,
    headers: { ...headers, ...headersParam },
    responseTypes: 'json',
    requestType: 'json',
  });
};
(getCompanyPermissionTypes as any).paramsOrder = [{ type: 'parameters' }];

export type getCompanyIdsWithActivePermissionsParameters = { permission: string };
export type getCompanyIdsWithActivePermissionsResponse = PinType<
  ApiResponsePromise<{
    responseCode: 200;
    data: number[];
    headers: null;
  }>,
  number[]
>;

export type getCompanyIdsWithActivePermissionsSeparateParams = {
  parameters: getCompanyIdsWithActivePermissionsParameters;
};
export type getCompanyIdsWithActivePermissionsParamsUnion = getCompanyIdsWithActivePermissionsParameters;
export type getCompanyIdsWithActivePermissions = {
  (arg: {
    params: PinType<getCompanyIdsWithActivePermissionsParamsUnion, getCompanyIdsWithActivePermissionsSeparateParams>;
    onUploadProgress?: (event: ApiProgressEvent) => void;
    signal?: AbortSignal;
    baseURL?: string;
    isInternal?: boolean;
    headers?: Record<string, string>;
    queryDefaults?: Partial<getCompanyIdsWithActivePermissionsParamsUnion> | false;
  }): getCompanyIdsWithActivePermissionsResponse;
};

export const getCompanyIdsWithActivePermissions: getCompanyIdsWithActivePermissions = async ({
  params,
  onUploadProgress,
  signal,
  baseURL,
  isInternal,
  headers: headersParam = {},
  queryDefaults,
  ...otherParams
}) => {
  if (params.permission === undefined) {
    throw new Error('Missing required parameter: permission');
  }

  const path = '/api/ws-permissions/permissions/company-ids';
  const pathInternal = '/api/permissions/company-ids';
  let body: any = {};
  const formData = new FormData();
  const urlParams = new URLSearchParams();
  if (params.permission !== undefined) {
    urlParams.append('permission', params.permission.toString());
  }

  const headers: Record<string, string> = {};
  headers['Accept'] = 'application/json';

  return makeRequest<ApiResponseArgs<getCompanyIdsWithActivePermissionsResponse>>({
    ...otherParams,
    method: 'GET',
    url: isInternal ? pathInternal : path,
    params: urlParams,
    data: {},
    onUploadProgress,
    signal,
    baseURL: baseURL || undefined,
    headers: { ...headers, ...headersParam },
    responseTypes: 'json',
    requestType: 'json',
  });
};
(getCompanyIdsWithActivePermissions as any).paramsOrder = [{ type: 'parameters' }];

export type getAccountsParameters = {
  /** the size of the page to be returned */ limit?: number;
  nameFilter?: string;
  /** sort direction */ order?: string;
  /** zero-based page index */ page?: number;
  /** comma separated list of sort properties */ sort?: string;
};
export type getAccountsResponse = PinType<
  ApiResponsePromise<{
    responseCode: 200;
    data: Types.PaginatedAccounts;
    headers: null;
  }>,
  Types.PaginatedAccounts
>;

export type getAccountsSeparateParams = {
  parameters: getAccountsParameters;
};
export type getAccountsParamsUnion = getAccountsParameters;
export type getAccounts = {
  (arg: {
    params: PinType<getAccountsParamsUnion, getAccountsSeparateParams>;
    onUploadProgress?: (event: ApiProgressEvent) => void;
    signal?: AbortSignal;
    baseURL?: string;
    isInternal?: boolean;
    headers?: Record<string, string>;
    queryDefaults?: Partial<getAccountsParamsUnion> | false;
  }): getAccountsResponse;
};

export const getAccounts: getAccounts = async ({
  params,
  onUploadProgress,
  signal,
  baseURL,
  isInternal,
  headers: headersParam = {},
  queryDefaults,
  ...otherParams
}) => {
  const path = '/api/ws-permissions/accounts';
  const pathInternal = '/api/accounts';
  let body: any = {};
  const formData = new FormData();
  const urlParams = new URLSearchParams();
  {
    const value = queryDefaults === false ? params.page : (params.page ?? queryDefaults?.['page']);
    if (value !== undefined) {
      urlParams.append('page', value.toString());
    }
  }
  {
    const value = queryDefaults === false ? params.limit : (params.limit ?? queryDefaults?.['limit']);
    if (value !== undefined) {
      urlParams.append('limit', value.toString());
    }
  }
  {
    const value = queryDefaults === false ? params.order : (params.order ?? queryDefaults?.['order']);
    if (value !== undefined) {
      urlParams.append('order', value.toString());
    }
  }
  {
    const value = queryDefaults === false ? params.sort : (params.sort ?? queryDefaults?.['sort']);
    if (value !== undefined) {
      urlParams.append('sort', value.toString());
    }
  }
  {
    const value = queryDefaults === false ? params.nameFilter : (params.nameFilter ?? queryDefaults?.['nameFilter']);
    if (value !== undefined) {
      urlParams.append('nameFilter', value.toString());
    }
  }

  const headers: Record<string, string> = {};
  headers['Accept'] = 'application/json';

  return makeRequest<ApiResponseArgs<getAccountsResponse>>({
    ...otherParams,
    method: 'GET',
    url: isInternal ? pathInternal : path,
    params: urlParams,
    data: {},
    onUploadProgress,
    signal,
    baseURL: baseURL || undefined,
    headers: { ...headers, ...headersParam },
    responseTypes: 'json',
    requestType: 'json',
  });
};
(getAccounts as any).paramsOrder = [{ type: 'parameters' }];

export type getAccountsByIdsResponse = PinType<
  ApiResponsePromise<{
    responseCode: 200;
    data: Types.AccountDto[];
    headers: null;
  }>,
  Types.AccountDto[]
>;

export type getAccountsByIdsSeparateParams = {
  body: IdAccountDto[];
};
export type getAccountsByIdsParamsUnion = { accountIds: IdAccountDto[] };
export type getAccountsByIds = {
  (arg: {
    params: PinType<getAccountsByIdsParamsUnion, getAccountsByIdsSeparateParams>;
    onUploadProgress?: (event: ApiProgressEvent) => void;
    signal?: AbortSignal;
    baseURL?: string;
    isInternal?: boolean;
    headers?: Record<string, string>;
    queryDefaults?: Partial<getAccountsByIdsParamsUnion> | false;
  }): getAccountsByIdsResponse;
};

export const getAccountsByIds: getAccountsByIds = async ({
  params,
  onUploadProgress,
  signal,
  baseURL,
  isInternal,
  headers: headersParam = {},
  queryDefaults,
  ...otherParams
}) => {
  if (params.accountIds === undefined) {
    throw new Error('Missing required body: accountIds');
  }

  const path = '/api/ws-permissions/accounts/by-ids';
  const pathInternal = '/api/accounts/by-ids';
  let body: any = {};
  const formData = new FormData();
  const urlParams = new URLSearchParams();
  if (params.accountIds !== undefined) {
    body = params.accountIds;
  }

  const headers: Record<string, string> = {};
  headers['Accept'] = 'application/json';
  headers['Content-Type'] = 'application/json';

  return makeRequest<ApiResponseArgs<getAccountsByIdsResponse>>({
    ...otherParams,
    method: 'POST',
    url: isInternal ? pathInternal : path,
    params: urlParams,
    data: encodeBody(body),
    onUploadProgress,
    signal,
    baseURL: baseURL || undefined,
    headers: { ...headers, ...headersParam },
    responseTypes: 'json',
    requestType: 'json',
  });
};
(getAccountsByIds as any).paramsOrder = [{ type: 'body', paramName: 'accountIds' }];

export type replaceAccountIdParameters = { accountId: IdAccountDto; newAccountId: IdAccountDto };
export type replaceAccountIdResponse = PinType<
  ApiResponsePromise<{
    responseCode: 202;
    data: void;
    headers: null;
  }>,
  void
>;

export type replaceAccountIdSeparateParams = {
  parameters: replaceAccountIdParameters;
};
export type replaceAccountIdParamsUnion = replaceAccountIdParameters;
export type replaceAccountId = {
  (arg: {
    params: PinType<replaceAccountIdParamsUnion, replaceAccountIdSeparateParams>;
    onUploadProgress?: (event: ApiProgressEvent) => void;
    signal?: AbortSignal;
    baseURL?: string;
    isInternal?: boolean;
    headers?: Record<string, string>;
    queryDefaults?: Partial<replaceAccountIdParamsUnion> | false;
  }): replaceAccountIdResponse;
};

/** Replace old account id with new one. */ export const replaceAccountId: replaceAccountId = async ({
  params,
  onUploadProgress,
  signal,
  baseURL,
  isInternal,
  headers: headersParam = {},
  queryDefaults,
  ...otherParams
}) => {
  if (params.accountId === undefined) {
    throw new Error('Missing required parameter: accountId');
  }
  if (params.newAccountId === undefined) {
    throw new Error('Missing required parameter: newAccountId');
  }

  const path = '/api/ws-permissions/accounts/replace-account-id';
  const pathInternal = '/api/accounts/replace-account-id';
  let body: any = {};
  const formData = new FormData();
  const urlParams = new URLSearchParams();
  if (params.accountId !== undefined) {
    urlParams.append('accountId', params.accountId.toString());
  }
  if (params.newAccountId !== undefined) {
    urlParams.append('newAccountId', params.newAccountId.toString());
  }

  const headers: Record<string, string> = {};
  headers['Accept'] = 'application/json';

  return makeRequest<ApiResponseArgs<replaceAccountIdResponse>>({
    ...otherParams,
    method: 'POST',
    url: isInternal ? pathInternal : path,
    params: urlParams,
    data: {},
    onUploadProgress,
    signal,
    baseURL: baseURL || undefined,
    headers: { ...headers, ...headersParam },
    responseTypes: 'void',
    requestType: 'json',
  });
};
(replaceAccountId as any).paramsOrder = [{ type: 'parameters' }];

export type getUserGroupsParameters = { companyId: IdCompanyDto; search?: string };
export type getUserGroupsResponse = PinType<
  ApiResponsePromise<{
    responseCode: 200;
    data: Types.UserGroupDto[];
    headers: null;
  }>,
  Types.UserGroupDto[]
>;

export type getUserGroupsSeparateParams = {
  parameters: getUserGroupsParameters;
};
export type getUserGroupsParamsUnion = getUserGroupsParameters;
export type getUserGroups = {
  (arg: {
    params: PinType<getUserGroupsParamsUnion, getUserGroupsSeparateParams>;
    onUploadProgress?: (event: ApiProgressEvent) => void;
    signal?: AbortSignal;
    baseURL?: string;
    isInternal?: boolean;
    headers?: Record<string, string>;
    queryDefaults?: Partial<getUserGroupsParamsUnion> | false;
  }): getUserGroupsResponse;
};

/** Get list of user groups */ export const getUserGroups: getUserGroups = async ({
  params,
  onUploadProgress,
  signal,
  baseURL,
  isInternal,
  headers: headersParam = {},
  queryDefaults,
  ...otherParams
}) => {
  if (params.companyId === undefined) {
    throw new Error('Missing required parameter: companyId');
  }

  const path = '/api/ws-permissions/user-groups';
  const pathInternal = '/api/user-groups';
  let body: any = {};
  const formData = new FormData();
  const urlParams = new URLSearchParams();
  if (params.companyId !== undefined) {
    urlParams.append('companyId', params.companyId.toString());
  }
  {
    const value = queryDefaults === false ? params.search : (params.search ?? queryDefaults?.['search']);
    if (value !== undefined) {
      urlParams.append('search', value.toString());
    }
  }

  const headers: Record<string, string> = {};
  headers['Accept'] = 'application/json';

  return makeRequest<ApiResponseArgs<getUserGroupsResponse>>({
    ...otherParams,
    method: 'GET',
    url: isInternal ? pathInternal : path,
    params: urlParams,
    data: {},
    onUploadProgress,
    signal,
    baseURL: baseURL || undefined,
    headers: { ...headers, ...headersParam },
    responseTypes: 'json',
    requestType: 'json',
  });
};
(getUserGroups as any).paramsOrder = [{ type: 'parameters' }];

export type createUserGroupResponse = PinType<
  ApiResponsePromise<{
    responseCode: 200;
    data: Types.UserGroupDto;
    headers: null;
  }>,
  Types.UserGroupDto
>;

export type createUserGroupSeparateParams = {
  body: Types.UserGroupForm;
};
export type createUserGroupParamsUnion = { form: Types.UserGroupForm };
export type createUserGroup = {
  (arg: {
    params: PinType<createUserGroupParamsUnion, createUserGroupSeparateParams>;
    onUploadProgress?: (event: ApiProgressEvent) => void;
    signal?: AbortSignal;
    baseURL?: string;
    isInternal?: boolean;
    headers?: Record<string, string>;
    queryDefaults?: Partial<createUserGroupParamsUnion> | false;
  }): createUserGroupResponse;
};

/** Create user group */ export const createUserGroup: createUserGroup = async ({
  params,
  onUploadProgress,
  signal,
  baseURL,
  isInternal,
  headers: headersParam = {},
  queryDefaults,
  ...otherParams
}) => {
  if (params.form === undefined) {
    throw new Error('Missing required body: form');
  }

  const path = '/api/ws-permissions/user-groups';
  const pathInternal = '/api/user-groups';
  let body: any = {};
  const formData = new FormData();
  const urlParams = new URLSearchParams();
  if (params.form !== undefined) {
    body = params.form;
  }

  const headers: Record<string, string> = {};
  headers['Accept'] = 'application/json';
  headers['Content-Type'] = 'application/json';

  return makeRequest<ApiResponseArgs<createUserGroupResponse>>({
    ...otherParams,
    method: 'POST',
    url: isInternal ? pathInternal : path,
    params: urlParams,
    data: encodeBody(body),
    onUploadProgress,
    signal,
    baseURL: baseURL || undefined,
    headers: { ...headers, ...headersParam },
    responseTypes: 'json',
    requestType: 'json',
  });
};
(createUserGroup as any).paramsOrder = [{ type: 'body', paramName: 'form' }];

export type updateUserGroupParameters = { id: IdUserGroupDto };
export type updateUserGroupResponse = PinType<
  ApiResponsePromise<{
    responseCode: 200;
    data: Types.UserGroupDto;
    headers: null;
  }>,
  Types.UserGroupDto
>;

export type updateUserGroupSeparateParams = {
  body: Types.UserGroupForm;
  parameters: updateUserGroupParameters;
};
export type updateUserGroupParamsUnion = { form: Types.UserGroupForm } & updateUserGroupParameters;
export type updateUserGroup = {
  (arg: {
    params: PinType<updateUserGroupParamsUnion, updateUserGroupSeparateParams>;
    onUploadProgress?: (event: ApiProgressEvent) => void;
    signal?: AbortSignal;
    baseURL?: string;
    isInternal?: boolean;
    headers?: Record<string, string>;
    queryDefaults?: Partial<updateUserGroupParamsUnion> | false;
  }): updateUserGroupResponse;
};

/** Update user group */ export const updateUserGroup: updateUserGroup = async ({
  params,
  onUploadProgress,
  signal,
  baseURL,
  isInternal,
  headers: headersParam = {},
  queryDefaults,
  ...otherParams
}) => {
  if (params.id === undefined) {
    throw new Error('Missing required parameter: id');
  }
  if (params.form === undefined) {
    throw new Error('Missing required body: form');
  }

  const path = '/api/ws-permissions/user-groups/{id}'.replaceAll('{id}', () => encodeURIComponent(String(params.id)));
  const pathInternal = '/api/user-groups/{id}'.replaceAll('{id}', () => encodeURIComponent(String(params.id)));
  let body: any = {};
  const formData = new FormData();
  const urlParams = new URLSearchParams();
  if (params.form !== undefined) {
    body = params.form;
  }

  const headers: Record<string, string> = {};
  headers['Accept'] = 'application/json';
  headers['Content-Type'] = 'application/json';

  return makeRequest<ApiResponseArgs<updateUserGroupResponse>>({
    ...otherParams,
    method: 'PUT',
    url: isInternal ? pathInternal : path,
    params: urlParams,
    data: encodeBody(body),
    onUploadProgress,
    signal,
    baseURL: baseURL || undefined,
    headers: { ...headers, ...headersParam },
    responseTypes: 'json',
    requestType: 'json',
  });
};
(updateUserGroup as any).paramsOrder = [{ type: 'body', paramName: 'form' }, { type: 'parameters' }];

export type deleteUserGroupParameters = { id: IdUserGroupDto };
export type deleteUserGroupResponse = PinType<
  ApiResponsePromise<{
    responseCode: 202;
    data: void;
    headers: null;
  }>,
  void
>;

export type deleteUserGroupSeparateParams = {
  parameters: deleteUserGroupParameters;
};
export type deleteUserGroupParamsUnion = deleteUserGroupParameters;
export type deleteUserGroup = {
  (arg: {
    params: PinType<deleteUserGroupParamsUnion, deleteUserGroupSeparateParams>;
    onUploadProgress?: (event: ApiProgressEvent) => void;
    signal?: AbortSignal;
    baseURL?: string;
    isInternal?: boolean;
    headers?: Record<string, string>;
    queryDefaults?: Partial<deleteUserGroupParamsUnion> | false;
  }): deleteUserGroupResponse;
};

/** Delete user group */ export const deleteUserGroup: deleteUserGroup = async ({
  params,
  onUploadProgress,
  signal,
  baseURL,
  isInternal,
  headers: headersParam = {},
  queryDefaults,
  ...otherParams
}) => {
  if (params.id === undefined) {
    throw new Error('Missing required parameter: id');
  }

  const path = '/api/ws-permissions/user-groups/{id}'.replaceAll('{id}', () => encodeURIComponent(String(params.id)));
  const pathInternal = '/api/user-groups/{id}'.replaceAll('{id}', () => encodeURIComponent(String(params.id)));
  let body: any = {};
  const formData = new FormData();
  const urlParams = new URLSearchParams();

  const headers: Record<string, string> = {};

  return makeRequest<ApiResponseArgs<deleteUserGroupResponse>>({
    ...otherParams,
    method: 'DELETE',
    url: isInternal ? pathInternal : path,
    params: urlParams,
    data: {},
    onUploadProgress,
    signal,
    baseURL: baseURL || undefined,
    headers: { ...headers, ...headersParam },
    responseTypes: 'void',
    requestType: 'json',
  });
};
(deleteUserGroup as any).paramsOrder = [{ type: 'parameters' }];

export type enableUserGroupParameters = { id: IdUserGroupDto; status: boolean };
export type enableUserGroupResponse = PinType<
  ApiResponsePromise<{
    responseCode: 200;
    data: Types.UserGroupDto;
    headers: null;
  }>,
  Types.UserGroupDto
>;

export type enableUserGroupSeparateParams = {
  parameters: enableUserGroupParameters;
};
export type enableUserGroupParamsUnion = enableUserGroupParameters;
export type enableUserGroup = {
  (arg: {
    params: PinType<enableUserGroupParamsUnion, enableUserGroupSeparateParams>;
    onUploadProgress?: (event: ApiProgressEvent) => void;
    signal?: AbortSignal;
    baseURL?: string;
    isInternal?: boolean;
    headers?: Record<string, string>;
    queryDefaults?: Partial<enableUserGroupParamsUnion> | false;
  }): enableUserGroupResponse;
};

/** Update user group */ export const enableUserGroup: enableUserGroup = async ({
  params,
  onUploadProgress,
  signal,
  baseURL,
  isInternal,
  headers: headersParam = {},
  queryDefaults,
  ...otherParams
}) => {
  if (params.id === undefined) {
    throw new Error('Missing required parameter: id');
  }
  if (params.status === undefined) {
    throw new Error('Missing required parameter: status');
  }

  const path = '/api/ws-permissions/user-groups/{id}/enable'.replaceAll('{id}', () =>
    encodeURIComponent(String(params.id)),
  );
  const pathInternal = '/api/user-groups/{id}/enable'.replaceAll('{id}', () => encodeURIComponent(String(params.id)));
  let body: any = {};
  const formData = new FormData();
  const urlParams = new URLSearchParams();
  if (params.status !== undefined) {
    urlParams.append('status', params.status.toString());
  }

  const headers: Record<string, string> = {};
  headers['Accept'] = 'application/json';

  return makeRequest<ApiResponseArgs<enableUserGroupResponse>>({
    ...otherParams,
    method: 'PUT',
    url: isInternal ? pathInternal : path,
    params: urlParams,
    data: {},
    onUploadProgress,
    signal,
    baseURL: baseURL || undefined,
    headers: { ...headers, ...headersParam },
    responseTypes: 'json',
    requestType: 'json',
  });
};
(enableUserGroup as any).paramsOrder = [{ type: 'parameters' }];

export type getGroupsByUserParameters = { userId: IdUserDto };
export type getGroupsByUserResponse = PinType<
  ApiResponsePromise<{
    responseCode: 200;
    data: Types.UserGroupDto[];
    headers: null;
  }>,
  Types.UserGroupDto[]
>;

export type getGroupsByUserSeparateParams = {
  parameters: getGroupsByUserParameters;
};
export type getGroupsByUserParamsUnion = getGroupsByUserParameters;
export type getGroupsByUser = {
  (arg: {
    params: PinType<getGroupsByUserParamsUnion, getGroupsByUserSeparateParams>;
    onUploadProgress?: (event: ApiProgressEvent) => void;
    signal?: AbortSignal;
    baseURL?: string;
    isInternal?: boolean;
    headers?: Record<string, string>;
    queryDefaults?: Partial<getGroupsByUserParamsUnion> | false;
  }): getGroupsByUserResponse;
};

/** Get list of user groups for selected user */ export const getGroupsByUser: getGroupsByUser = async ({
  params,
  onUploadProgress,
  signal,
  baseURL,
  isInternal,
  headers: headersParam = {},
  queryDefaults,
  ...otherParams
}) => {
  if (params.userId === undefined) {
    throw new Error('Missing required parameter: userId');
  }

  const path = '/api/ws-permissions/user-groups/by-user';
  const pathInternal = '/api/user-groups/by-user';
  let body: any = {};
  const formData = new FormData();
  const urlParams = new URLSearchParams();
  if (params.userId !== undefined) {
    urlParams.append('userId', params.userId.toString());
  }

  const headers: Record<string, string> = {};
  headers['Accept'] = 'application/json';

  return makeRequest<ApiResponseArgs<getGroupsByUserResponse>>({
    ...otherParams,
    method: 'GET',
    url: isInternal ? pathInternal : path,
    params: urlParams,
    data: {},
    onUploadProgress,
    signal,
    baseURL: baseURL || undefined,
    headers: { ...headers, ...headersParam },
    responseTypes: 'json',
    requestType: 'json',
  });
};
(getGroupsByUser as any).paramsOrder = [{ type: 'parameters' }];

export type assignUserGroupsParameters = { userId: IdUserDto };
export type assignUserGroupsResponse = PinType<
  ApiResponsePromise<{
    responseCode: 202;
    data: void;
    headers: null;
  }>,
  void
>;

export type assignUserGroupsSeparateParams = {
  body: IdUserGroupDto[];
  parameters: assignUserGroupsParameters;
};
export type assignUserGroupsParamsUnion = { userGroupIds: IdUserGroupDto[] } & assignUserGroupsParameters;
export type assignUserGroups = {
  (arg: {
    params: PinType<assignUserGroupsParamsUnion, assignUserGroupsSeparateParams>;
    onUploadProgress?: (event: ApiProgressEvent) => void;
    signal?: AbortSignal;
    baseURL?: string;
    isInternal?: boolean;
    headers?: Record<string, string>;
    queryDefaults?: Partial<assignUserGroupsParamsUnion> | false;
  }): assignUserGroupsResponse;
};

/** Assign user groups to a user */ export const assignUserGroups: assignUserGroups = async ({
  params,
  onUploadProgress,
  signal,
  baseURL,
  isInternal,
  headers: headersParam = {},
  queryDefaults,
  ...otherParams
}) => {
  if (params.userGroupIds === undefined) {
    throw new Error('Missing required body: userGroupIds');
  }
  if (params.userId === undefined) {
    throw new Error('Missing required parameter: userId');
  }

  const path = '/api/ws-permissions/user-groups/by-user';
  const pathInternal = '/api/user-groups/by-user';
  let body: any = {};
  const formData = new FormData();
  const urlParams = new URLSearchParams();
  if (params.userGroupIds !== undefined) {
    body = params.userGroupIds;
  }
  if (params.userId !== undefined) {
    urlParams.append('userId', params.userId.toString());
  }

  const headers: Record<string, string> = {};
  headers['Content-Type'] = 'application/json';

  return makeRequest<ApiResponseArgs<assignUserGroupsResponse>>({
    ...otherParams,
    method: 'PUT',
    url: isInternal ? pathInternal : path,
    params: urlParams,
    data: encodeBody(body),
    onUploadProgress,
    signal,
    baseURL: baseURL || undefined,
    headers: { ...headers, ...headersParam },
    responseTypes: 'void',
    requestType: 'json',
  });
};
(assignUserGroups as any).paramsOrder = [{ type: 'body', paramName: 'userGroupIds' }, { type: 'parameters' }];

export * from './types';
export * from './constants';
